import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Search, Loading, Modal, Tooltip, Pagination, Dropdown, TextInput, NumberInput, InlineNotification } from '@carbon/react';
import { Add, Settings, TrashCan, Edit, View, CheckmarkFilled, CloudOffline, Wifi, Time, List, Grid as GridIcon, Connection, Information, Analytics, WarningAltFilled, Checkmark, LightFilled, Renew } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useGatewaysOptimized } from '../hooks/useGatewaysOptimized';
import { formatRelativeTime } from '../lib/timeUtils';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import { analyzeScreenData } from '../services/aiService';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useSensorContexts } from '../hooks/useSensorContexts';
import { enrichGatewaysWithContexts } from '../utils/intelligenceContext';
import GatewayTypeTags from '../components/GatewayTypeTags';
import {
  buildGetCfgCommand,
  fetchGatewayConfig,
  normalizeGatewayMacHex,
  saveGatewayConfig,
  sendMqttAdminCommand,
} from '../features/gateway-admin/gatewayAdminApi';
import { getGatewayAdminCapabilities } from '../utils/gatewayAdminCapabilities';
import { getGatewayProductFamily } from '../utils/gatewayProductLabel';
import {
  FILTER_MODE_ITEMS,
  buildSettingsSaveCommand,
  configSourceLabel,
  getDefaultSettingsForm,
  mergeFormIntoConfig,
  parseGatewayConfigFields,
} from '../utils/gatewayConfigForm';

const GatewayListPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'online', 'warning', 'offline'
  const { gateways, isLoading, error, refetch } = useGatewaysOptimized();
  const { sensors } = useSensorsOptimized();
  const { sensorContexts } = useSensorContexts();
  const [gatewayToDelete, setGatewayToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [statusInfoModalOpen, setStatusInfoModalOpen] = useState(false);
  
  // Pagination state (only used for table view)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // AI Analysis state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiError, setAiError] = useState(null);

  // Gateway Settings Modal state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedGatewayForSettings, setSelectedGatewayForSettings] = useState(null);
  const [connectivityType, setConnectivityType] = useState('mqtt'); // 'mqtt' or 'http'
  const [uploadInterval, setUploadInterval] = useState(120); // seconds
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configError, setConfigError] = useState(null);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [filterRssi, setFilterRssi] = useState(-100);
  const [filterMac, setFilterMac] = useState('');
  const [filterMode, setFilterMode] = useState('off');
  const [configSource, setConfigSource] = useState(null);
  const [settingsNotification, setSettingsNotification] = useState(null); // { kind, title, subtitle }

  const applySettingsForm = (config, gateway, source) => {
    const fields = parseGatewayConfigFields(config, gateway);
    setConnectivityType(fields.connectivityType);
    setUploadInterval(fields.uploadInterval);
    setFilterName(fields.filterName);
    setFilterRssi(fields.filterRssi);
    setFilterMac(fields.filterMac);
    setFilterMode(fields.filterMode);
    setConfigSource(source);
  };

  const resetSettingsForm = (gateway) => {
    const defaults = getDefaultSettingsForm(gateway);
    setConnectivityType(defaults.connectivityType);
    setUploadInterval(defaults.uploadInterval);
    setFilterName(defaults.filterName);
    setFilterRssi(defaults.filterRssi);
    setFilterMac(defaults.filterMac);
    setFilterMode(defaults.filterMode);
    setConfigSource('default');
  };

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const filteredGateways = useMemo(() => {
    if (!gateways) return [];
    return gateways.filter(gateway => {
      const matchesSearch = (gateway.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (gateway.mac_address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (gateway.ip_address || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || gateway.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [gateways, searchTerm, statusFilter]);

  // Pagination calculations (only for table view)
  const totalItems = filteredGateways.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedGateways = viewMode === 'table' 
    ? filteredGateways.slice(startIndex, endIndex)
    : filteredGateways;

  // Reset to page 1 when search term changes, view mode changes, or status filter changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, viewMode, statusFilter]);

  // 3-tier status matching SensorCard logic: online (<15min), warning (15-60min), offline (>60min)
  const getStatusColor = (status) => {
    if (status === 'online') return '#24a148'; // Green
    if (status === 'warning') return '#f1c21b'; // Yellow
    return '#da1e28'; // Red
  };

  const getStatusIcon = (status) => {
    if (status === 'online') {
      return <CheckmarkFilled size={16} style={{ color: '#24a148' }} />; // Green - online
    } else if (status === 'warning') {
      return <Time size={16} style={{ color: '#f1c21b' }} />; // Yellow - stale/warning
    }
    return <CloudOffline size={16} style={{ color: '#da1e28' }} />; // Red - offline
  };

  const getStatusLabel = (status) => {
    if (status === 'warning') return 'stale';
    return status || 'offline';
  };

  const formatLastSeen = (lastSeenAt) => {
    if (!lastSeenAt) return 'Never';
    const date = new Date(lastSeenAt);
    if (!date || isNaN(date.getTime())) return 'Never';
    
    // Format actual time in local timezone: "Jan 18, 10:10 PM"
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    
    // Format time in local timezone
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const time = `${hours}:${minutesStr} ${ampm}`;
    
    const actualTime = `${month} ${day}, ${time}`;
    
    // Format relative time: "2 mins ago"
    const relativeTime = formatRelativeTime(date);
    
    return `${actualTime} (${relativeTime})`;
  };

  const openDeleteModal = (gateway) => {
    setGatewayToDelete(gateway);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setGatewayToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDeleteGateway = async () => {
    if (!user || !gatewayToDelete) {
      setDeleteError('You must be signed in to delete a gateway.');
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteError(null);

      const hasFailedDeletion =
        gatewayToDelete.deleted_at && gatewayToDelete.deletion_status === 'failed';
      const nowIso = new Date().toISOString();

      const updateData = {
        deleted_at: nowIso,
        deletion_status: 'pending',
        deletion_started_at: nowIso,
      };

      if (gatewayToDelete.marked_for_deletion) {
        updateData.marked_for_deletion = true;
        updateData.deletion_marked_at = nowIso;
      }

      const { error: updateError } = await supabase
        .from('gateways')
        .update(updateData)
        .eq('id', gatewayToDelete.id)
        .eq('user_id', user.id);

      if (updateError) {
        setDeleteError(updateError.message || 'Failed to delete gateway. Please try again.');
        return;
      }

      // Refresh list; background deletion runs in Supabase
      setIsDeleteModalOpen(false);
      setGatewayToDelete(null);
      await refetch();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete gateway. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const loadGatewayConfigForModal = async (gateway, { forceRefresh = false } = {}) => {
    setConfigError(null);
    setLoadingConfig(true);

    let loadedConfig = null;

    const cached =
      gateway?.last_known_config && typeof gateway.last_known_config === 'object'
        ? gateway.last_known_config
        : null;

    if (cached && !forceRefresh) {
      loadedConfig = cached;
      setCurrentConfig(cached);
      applySettingsForm(cached, gateway, 'cache');
    } else if (gateway?.id && !forceRefresh) {
      try {
        const dbConfig = await fetchGatewayConfig(gateway.id);
        if (dbConfig?.success && dbConfig.config) {
          loadedConfig = dbConfig.config;
          setCurrentConfig(dbConfig.config);
          applySettingsForm(dbConfig.config, gateway, 'db');
        }
      } catch (err) {
        console.warn('Could not preload gateway config from database:', err);
      }
    }

    try {
      const macHex = normalizeGatewayMacHex(gateway);
      if (!macHex && !gateway.id) {
        throw new Error('Gateway MAC address not found');
      }

      const result = await sendMqttAdminCommand(gateway, {
        command: buildGetCfgCommand(Math.floor(Date.now() / 1000) % 100000),
        timeout: 45,
        waitForResponse: true,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to load gateway configuration');
      }

      const config = result.response || {};
      loadedConfig = config;
      setCurrentConfig(config);
      applySettingsForm(config, gateway, 'mqtt');

      if (gateway.id) {
        await saveGatewayConfig(gateway.id, config).catch((err) => {
          console.warn('Could not persist gateway config:', err);
        });
      }
    } catch (error) {
      console.error('Error loading gateway config:', error);
      const msg = error.message || 'Failed to load gateway configuration';
      const isTimeout =
        resultTimedOut(msg) ||
        msg.includes('408') ||
        msg.includes('offline') ||
        msg.includes('unreachable');

      if (!loadedConfig && gateway?.id) {
        try {
          const dbConfig = await fetchGatewayConfig(gateway.id);
          if (dbConfig?.success && dbConfig.config) {
            loadedConfig = dbConfig.config;
            setCurrentConfig(dbConfig.config);
            applySettingsForm(dbConfig.config, gateway, 'db');
            setConfigError(
              isTimeout
                ? 'Gateway did not respond in time. Showing last saved configuration — you can edit and save to push changes via MQTT.'
                : `${msg}. Showing last saved configuration.`,
            );
            return;
          }
        } catch (dbErr) {
          console.warn('Database config fallback failed:', dbErr);
        }
      }

      if (!loadedConfig) {
        resetSettingsForm(gateway);
      }

      setConfigError(
        isTimeout
          ? 'Gateway did not respond in time. You can still edit and save settings below (command will be sent via MQTT).'
          : msg,
      );
    } finally {
      setLoadingConfig(false);
    }
  };

  const resultTimedOut = (msg) =>
    msg.includes('did not respond in time') || /did not respond/i.test(msg);

  const openSettingsModal = async (gateway) => {
    setSelectedGatewayForSettings(gateway);
    setSettingsModalOpen(true);
    setCurrentConfig(null);
    setSettingsNotification(null);
    resetSettingsForm(gateway);
    await loadGatewayConfigForModal(gateway);
  };

  const closeSettingsModal = () => {
    if (savingConfig) return;
    setSettingsModalOpen(false);
    setSelectedGatewayForSettings(null);
    setConfigError(null);
    setCurrentConfig(null);
    setConnectivityType('mqtt');
    setUploadInterval(120);
    setFilterName('');
    setFilterRssi(-100);
    setFilterMac('');
    setFilterMode('off');
    setConfigSource(null);
  };

  const handleSaveSettings = async () => {
    if (!selectedGatewayForSettings) return;

    setSavingConfig(true);
    setConfigError(null);

    try {
      const gateway = selectedGatewayForSettings;
      const macHex = normalizeGatewayMacHex(gateway);
      const form = {
        connectivityType,
        uploadInterval,
        filterRssi,
        filterMac,
        filterName,
        filterMode,
      };
      const command = buildSettingsSaveCommand(form, gateway);

      const result = await sendMqttAdminCommand(gateway, {
        command,
        timeout: 30,
        waitForResponse: false,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to save gateway configuration');
      }

      if (gateway.id) {
        const merged = mergeFormIntoConfig(currentConfig, form, gateway);
        await saveGatewayConfig(gateway.id, merged).catch((err) => {
          console.warn('Could not save gateway config copy:', err);
        });
      }

      // When waitForResponse: false, result.sent is true and there is no result.response
      const gwResponse = result.response || {};
      const gwResult = gwResponse.rslt ?? gwResponse.result;
      const isGwSuccess = gwResult === 0 || gwResult === 'succ';

      if (result.sent) {
        setSettingsNotification({
          kind: 'info',
          title: 'Command Sent',
          subtitle: 'Configuration was sent to the gateway. Reopen Settings to verify it was applied.',
        });
      } else if (isGwSuccess) {
        setSettingsNotification({
          kind: 'success',
          title: 'Configuration Sent',
          subtitle: `Gateway ${gateway.name || macHex} confirmed the update (rptPeriod: ${uploadInterval}s).`,
        });
      } else if (gwResult !== undefined) {
        setSettingsNotification({
          kind: 'warning',
          title: 'Gateway Responded',
          subtitle: `Gateway returned result: ${gwResult}. The configuration may not have been applied.`,
        });
      } else {
        setSettingsNotification({
          kind: 'info',
          title: 'Command Sent',
          subtitle: 'The command was sent to the gateway. Reopen Settings to verify it was applied.',
        });
      }

      // Close modal and refresh
      closeSettingsModal();
      await refetch();

    } catch (error) {
      console.error('Error saving gateway config:', error);
      setConfigError(error.message || 'Failed to save gateway configuration');
      setSettingsNotification({
        kind: 'error',
        title: 'Configuration Failed',
        subtitle: error.message || 'Failed to save gateway configuration',
      });
    } finally {
      setSavingConfig(false);
    }
  };

  const handleAIAnalysis = async () => {
    setAiModalOpen(true);
    setAiLoading(true);
    setAiError(null);
    setAiAnalysis(null);

    try {
      // Prepare gateway data for AI analysis
      const gatewayData = enrichGatewaysWithContexts(
        gateways.map((gateway) => ({
          id: gateway.id,
          name: gateway.name,
          mac_address: gateway.mac_address,
          ip_address: gateway.ip_address,
          status: gateway.status,
          source: gateway.source,
          connectedDevices: gateway.connectedDevices,
          onlineDevices: gateway.onlineDevices,
          warningDevices: gateway.warningDevices || 0,
          offlineDevices: gateway.offlineDevices,
          last_seen_at: gateway.last_seen_at,
          minutesAgo: gateway.minutesAgo,
          health: gateway.health,
        })),
        sensors,
        sensorContexts,
      );

      console.log('🤖 Analyzing gateways data...', {
        gatewayCount: gatewayData.length,
        withContext: gatewayData.filter((g) => g.hasIntelligenceContext).length,
      });

      const result = await analyzeScreenData(gatewayData, 'gateways');
      setAiAnalysis(result);
    } catch (error) {
      console.error('❌ AI Analysis failed:', error);
      setAiError(error.message || 'Failed to analyze data. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Loading description="Loading gateways..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '24px' }}>
        <div style={{ color: '#da1e28', padding: '16px', backgroundColor: bgSecondary, borderRadius: '4px' }}>
          Error loading gateways: {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="gateway-list-page">
      {/* Settings notification banner */}
      {settingsNotification && (
        <div style={{ position: 'fixed', top: '80px', right: '16px', zIndex: 9999, maxWidth: '400px' }}>
          <InlineNotification
            kind={settingsNotification.kind}
            title={settingsNotification.title}
            subtitle={settingsNotification.subtitle}
            onCloseButtonClick={() => setSettingsNotification(null)}
            lowContrast
          />
        </div>
      )}
      {/* Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '24px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div>
                    <h1 style={{ color: textPrimary, fontSize: '1.75rem', fontWeight: 600, marginBottom: '4px' }}>Gateways</h1>
                    <p style={{ color: textSecondary, fontSize: '0.875rem' }}>{filteredGateways.length} of {gateways?.length || 0} gateways</p>
                  </div>
                  <Tooltip align="bottom" label="Gateway status information">
                    <button
                      onClick={() => setStatusInfoModalOpen(true)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        color: textSecondary
                      }}
                    >
                      <Information size={20} />
                    </button>
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px', width: '150px' }}>
                  <Button kind="primary" renderIcon={Add} onClick={() => navigate('/dashboard/gateways/add')} data-testid="add-gateway" style={{ whiteSpace: 'nowrap', width: '100%' }}>
                    Add Gateway
                  </Button>
                  <Button
                    kind="secondary"
                    renderIcon={Settings}
                    onClick={() => navigate('/dashboard/gateways/config')}
                    data-testid="gateway-config-ota"
                    style={{ whiteSpace: 'nowrap', width: '100%' }}
                  >
                    Config & OTA
                  </Button>
                  <Button 
                    kind="primary" 
                    renderIcon={Analytics} 
                    onClick={handleAIAnalysis}
                    data-testid="ai-analysis"
                    style={{ 
                      whiteSpace: 'nowrap',
                      width: '100%',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: '#ffffff',
                      border: 'none'
                    }}
                  >
                    AI Analysis
                  </Button>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
                  <Search
                    labelText="Search"
                    placeholder="Search gateways..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="lg"
                    data-testid="search-gateways"
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    kind={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={GridIcon}
                    iconDescription="Card view"
                    onClick={() => setViewMode('cards')}
                  />
                  <Button
                    kind={viewMode === 'table' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={List}
                    iconDescription="Table view"
                    onClick={() => setViewMode('table')}
                  />
                </div>
                <div style={{ minWidth: '200px', flexShrink: 0, marginLeft: 'auto' }}>
                  <Dropdown
                    id="status-filter"
                    titleText="Filter by Status"
                    label="Select status"
                    items={['All Gateways', 'Online', 'Warning', 'Offline']}
                    selectedItem={statusFilter === 'all' ? 'All Gateways' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                    onChange={({ selectedItem }) => {
                      if (selectedItem) {
                        // Map display text back to status value
                        const statusMap = {
                          'All Gateways': 'all',
                          'Online': 'online',
                          'Warning': 'warning',
                          'Offline': 'offline',
                        };
                        setStatusFilter(statusMap[selectedItem] || 'all');
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '24px 0' }}>
        <Grid>
          {filteredGateways.length === 0 ? (
            <Column lg={16} md={8} sm={4}>
              <div style={{ padding: '48px 0', textAlign: 'center' }}>
                <p style={{ color: textSecondary, fontSize: '1rem', marginBottom: 0 }}>
                  {searchTerm
                    ? 'No gateways found matching your search.'
                    : 'No gateways found. Add your first gateway to get started.'}
                </p>
              </div>
            </Column>
          ) : viewMode === 'cards' ? (
            // Card View
            filteredGateways.map((gateway) => (
              <Column lg={8} md={4} sm={4} key={gateway.id}>
                <Tile
                  style={{
                    backgroundColor: bgSecondary,
                    border: `1px solid ${borderColor}`,
                    borderLeft: `4px solid ${getStatusColor(gateway.status)}`,
                    padding: '0',
                    marginBottom: '16px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                  }}
                  data-testid={`gateway-card-${gateway.id}`}
                  onClick={() => navigate(`/dashboard/gateways/${gateway.id}`)}
                >
                  {/* Card Header */}
                  <div style={{ padding: '16px', borderBottom: `1px solid ${borderColor}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ backgroundColor: '#0f62fe', borderRadius: '8px', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Wifi size={20} style={{ color: '#ffffff' }} />
                        </div>
                        <div>
                          <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0, marginBottom: '4px' }}>
                            {gateway.name || `Gateway ${gateway.mac_address?.slice(-6) || gateway.id.slice(0, 8)}`}
                          </h3>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <GatewayTypeTags gateway={gateway} />
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {getStatusIcon(gateway.status)}
                        <span style={{ color: getStatusColor(gateway.status), fontSize: '0.875rem', textTransform: 'capitalize' }}>
                          {getStatusLabel(gateway.status)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Body - Stats */}
                  <div style={{ padding: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                      <div>
                        <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>MAC Address</p>
                        <p style={{ color: textPrimary, fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>{gateway.mac_address || 'N/A'}</p>
                      </div>
                      <div>
                        <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>Connected Devices</p>
                        {gateway.connectedDevices === 0 ? (
                          <p style={{ color: textSecondary, fontWeight: 600, fontSize: '1.25rem', margin: 0 }}>0</p>
                        ) : (gateway.offlineDevices === 0 && gateway.warningDevices === 0) ? (
                          <p style={{ color: '#24a148', fontWeight: 600, fontSize: '1.25rem', margin: 0 }}>
                            {gateway.onlineDevices || gateway.connectedDevices} Online
                          </p>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {(gateway.onlineDevices || 0) > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <CheckmarkFilled size={14} style={{ color: '#24a148' }} />
                                <span style={{ color: '#24a148', fontWeight: 600, fontSize: '1rem' }}>
                                  {gateway.onlineDevices} Online
                                </span>
                              </div>
                            )}
                            {(gateway.warningDevices || 0) > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Time size={14} style={{ color: '#f1c21b' }} />
                                <span style={{ color: '#f1c21b', fontWeight: 600, fontSize: '1rem' }}>
                                  {gateway.warningDevices} Stale
                                </span>
                              </div>
                            )}
                            {(gateway.offlineDevices || 0) > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <CloudOffline size={14} style={{ color: '#da1e28' }} />
                                <span style={{ color: '#da1e28', fontWeight: 600, fontSize: '1rem' }}>
                                  {gateway.offlineDevices} Offline
                                </span>
                              </div>
                            )}
                            <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0, marginTop: '2px' }}>
                              {gateway.connectedDevices} Total
                            </p>
                          </div>
                        )}
                      </div>
                      <div>
                        <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>IP Address</p>
                        <p style={{ color: textPrimary, fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>{gateway.ip_address || 'N/A'}</p>
                      </div>
                      <div>
                        <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>Created</p>
                        <p style={{ color: textPrimary, fontSize: '0.875rem', margin: 0 }}>
                          {gateway.created_at ? formatRelativeTime(new Date(gateway.created_at)) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '4px', color: textSecondary, fontSize: '0.875rem' }}>
                      <Time size={14} />
                      <span>Last seen: {formatLastSeen(gateway.last_seen_at)}</span>
                    </div>
                  </div>

                  {/* Card Footer - Actions */}
                  <div style={{ padding: '12px 16px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button 
                      kind="ghost" 
                      size="sm" 
                      renderIcon={View} 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/gateways/${gateway.id}`);
                      }} 
                      data-testid={`view-${gateway.id}`}
                    >
                      View
                    </Button>
                    <Button 
                      kind="ghost" 
                      size="sm" 
                      renderIcon={Settings} 
                      onClick={(e) => {
                        e.stopPropagation();
                        openSettingsModal(gateway);
                      }}
                      data-testid={`settings-${gateway.id}`}
                    >
                      Settings
                    </Button>
                    <Button 
                      kind="danger--ghost" 
                      size="sm" 
                      renderIcon={TrashCan} 
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(gateway);
                      }}
                      data-testid={`delete-${gateway.id}`}
                    >
                      Remove
                    </Button>
                  </div>
                </Tile>
              </Column>
            ))
          ) : (
            // Table View
            <Column lg={16} md={8} sm={4}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                  <thead>
                    <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Status</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Gateway</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>MAC Address</th>
                      <th style={{ padding: '16px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Devices</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>IP Address</th>
                      <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Last Seen</th>
                      <th style={{ padding: '16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedGateways.map((gateway, idx) => (
                      <tr
                        key={gateway.id}
                        style={{
                          borderBottom: `1px solid ${borderColor}`,
                          backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary,
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/dashboard/gateways/${gateway.id}`)}
                        data-testid={`gateway-row-${gateway.id}`}
                      >
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {getStatusIcon(gateway.status)}
                            <span style={{ color: getStatusColor(gateway.status), fontSize: '0.875rem', textTransform: 'capitalize' }}>
                              {getStatusLabel(gateway.status)}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div>
                            <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '2px' }}>
                              {gateway.name || `Gateway ${gateway.mac_address?.slice(-6) || gateway.id.slice(0, 8)}`}
                            </p>
                            <GatewayTypeTags gateway={gateway} />
                          </div>
                        </td>
                        <td style={{ padding: '16px', color: textSecondary, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {gateway.mac_address || 'N/A'}
                        </td>
                        <td style={{ padding: '16px', textAlign: 'center' }}>
                          {gateway.connectedDevices === 0 ? (
                            <span style={{ color: textSecondary, fontWeight: 600, fontSize: '1.125rem' }}>0</span>
                          ) : (gateway.offlineDevices === 0 && gateway.warningDevices === 0) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <CheckmarkFilled size={14} style={{ color: '#24a148' }} />
                                <span style={{ color: '#24a148', fontWeight: 600, fontSize: '1rem' }}>
                                  {gateway.onlineDevices || gateway.connectedDevices}
                                </span>
                              </div>
                              <span style={{ color: textSecondary, fontSize: '0.75rem' }}>All Online</span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                              {(gateway.onlineDevices || 0) > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <CheckmarkFilled size={12} style={{ color: '#24a148' }} />
                                  <span style={{ color: '#24a148', fontWeight: 600, fontSize: '0.875rem' }}>
                                    {gateway.onlineDevices} Online
                                  </span>
                                </div>
                              )}
                              {(gateway.warningDevices || 0) > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <Time size={12} style={{ color: '#f1c21b' }} />
                                  <span style={{ color: '#f1c21b', fontWeight: 600, fontSize: '0.875rem' }}>
                                    {gateway.warningDevices} Stale
                                  </span>
                                </div>
                              )}
                              {(gateway.offlineDevices || 0) > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <CloudOffline size={12} style={{ color: '#da1e28' }} />
                                  <span style={{ color: '#da1e28', fontWeight: 600, fontSize: '0.875rem' }}>
                                    {gateway.offlineDevices} Offline
                                  </span>
                                </div>
                              )}
                              <span style={{ color: textSecondary, fontSize: '0.75rem' }}>
                                {gateway.connectedDevices} Total
                              </span>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '16px', color: textSecondary, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {gateway.ip_address || 'N/A'}
                        </td>
                        <td style={{ padding: '16px', color: textSecondary, fontSize: '0.875rem' }}>
                          {formatLastSeen(gateway.last_seen_at)}
                        </td>
                        <td style={{ padding: '16px' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <Button 
                              kind="ghost" 
                              size="sm" 
                              hasIconOnly 
                              renderIcon={View} 
                              iconDescription="View" 
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/gateways/${gateway.id}`);
                              }} 
                            />
                            <Button 
                              kind="ghost" 
                              size="sm" 
                              hasIconOnly 
                              renderIcon={Settings} 
                              iconDescription="Settings"
                              onClick={(e) => {
                                e.stopPropagation();
                                openSettingsModal(gateway);
                              }}
                            />
                            <Button 
                              kind="danger--ghost" 
                              size="sm" 
                              hasIconOnly 
                              renderIcon={TrashCan} 
                              iconDescription="Remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteModal(gateway);
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination - only show for table view */}
              {totalItems > 0 && (
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    backwardText="Previous"
                    forwardText="Next"
                    itemsPerPageText="Items per page:"
                    page={page}
                    pageNumberText="Page Number"
                    pageSize={pageSize}
                    pageSizes={[10, 20, 30, 40, 50]}
                    size="md"
                    totalItems={totalItems}
                    onChange={({ page, pageSize }) => {
                      setPage(page);
                      setPageSize(pageSize);
                    }}
                  />
                </div>
              )}
            </Column>
          )}
        </Grid>
      </div>
      {/* Delete Gateway Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading={
          gatewayToDelete?.deletion_status === 'failed'
            ? 'Retry Gateway Deletion'
            : 'Delete Gateway'
        }
        primaryButtonText={
          gatewayToDelete?.deletion_status === 'failed' ? 'Retry Delete' : 'Delete'
        }
        secondaryButtonText="Cancel"
        danger
        onRequestClose={closeDeleteModal}
        onRequestSubmit={handleConfirmDeleteGateway}
        primaryButtonDisabled={isDeleting}
      >
        <p style={{ marginBottom: '1rem' }}>
          {gatewayToDelete?.deletion_status === 'failed'
            ? `Previous deletion attempt failed. Do you want to retry deleting "${gatewayToDelete?.name || 'this gateway'}"? This will reset the deletion status and begin the deletion process again.`
            : `Are you sure you want to delete "${gatewayToDelete?.name || 'this gateway'}"? This will permanently remove the gateway and all its data from your account. This action cannot be undone.`}
        </p>
        {deleteError && (
          <p style={{ color: '#da1e28', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            {deleteError}
          </p>
        )}
        {isDeleting && (
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Deleting gateway...
          </p>
        )}
      </Modal>

      {/* Gateway Status Information Modal */}
      <Modal
        open={statusInfoModalOpen}
        modalHeading="Gateway Status Guide"
        modalLabel="Information"
        passiveModal
        onRequestClose={() => setStatusInfoModalOpen(false)}
        size="md"
      >
        <div style={{ marginBottom: '24px' }}>
          <p style={{ marginBottom: '16px', color: textPrimary }}>
            Gateway status indicates how recently data was received. The status is determined by the time since the last data transmission:
          </p>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '100px' }}>
                <CheckmarkFilled size={16} style={{ color: '#24a148' }} />
                <span style={{ color: '#24a148', fontWeight: 600 }}>Online</span>
              </div>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px', color: textPrimary }}>Data within 15 minutes</p>
                <p style={{ fontSize: '0.875rem', color: textSecondary }}>
                  Gateway is actively transmitting data. Everything is working normally.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '100px' }}>
                <Time size={16} style={{ color: '#f1c21b' }} />
                <span style={{ color: '#f1c21b', fontWeight: 600 }}>Stale</span>
              </div>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px', color: textPrimary }}>Data 15-60 minutes old</p>
                <p style={{ fontSize: '0.875rem', color: textSecondary }}>
                  Gateway hasn't sent data recently. This may indicate a temporary connection issue or the gateway is in sleep mode.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '100px' }}>
                <CloudOffline size={16} style={{ color: '#da1e28' }} />
                <span style={{ color: '#da1e28', fontWeight: 600 }}>Offline</span>
              </div>
              <div>
                <p style={{ fontWeight: 600, marginBottom: '4px', color: textPrimary }}>No data for over 60 minutes</p>
                <p style={{ fontSize: '0.875rem', color: textSecondary }}>
                  Gateway is not transmitting data. Check power, network connectivity, and gateway configuration.
                </p>
              </div>
            </div>
          </div>

          <div style={{ 
            marginTop: '24px', 
            padding: '12px', 
            backgroundColor: isDark ? '#262626' : '#f4f4f4',
            borderRadius: '4px'
          }}>
            <p style={{ fontSize: '0.875rem', color: textSecondary }}>
              <strong>Tip:</strong> Gateways automatically collect data from nearby sensors and transmit it to the cloud. 
              Ensure your gateway has stable power and network connectivity for reliable data transmission.
            </p>
          </div>

          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            backgroundColor: isDark ? '#262626' : '#f4f4f4',
            borderRadius: '4px'
          }}>
            <p style={{ fontSize: '0.875rem', fontWeight: 600, color: textPrimary, marginBottom: '8px' }}>
              Connected Devices Status
            </p>
            <p style={{ fontSize: '0.875rem', color: textSecondary }}>
              The device count shows how many sensors are assigned to each gateway and their connection status based on the same time thresholds.
            </p>
          </div>
        </div>
      </Modal>

      {/* AI Analysis Modal */}
      <Modal
        open={aiModalOpen}
        modalHeading="AI Gateway Analysis"
        passiveModal
        onRequestClose={() => {
          setAiModalOpen(false);
          setAiAnalysis(null);
          setAiError(null);
        }}
        size="lg"
      >
        <div style={{ minHeight: '300px' }}>
          {aiLoading ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '48px',
              gap: '16px'
            }}>
              <Loading description="Analyzing your gateway data..." withOverlay={false} />
              <p style={{ color: textSecondary, textAlign: 'center' }}>
                Analyzing {gateways.length} gateways...
              </p>
              <p style={{ color: textSecondary, fontSize: '0.875rem', textAlign: 'center' }}>
                This may take a few moments
              </p>
            </div>
          ) : aiError ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              padding: '48px',
              gap: '16px'
            }}>
              <WarningAltFilled size={64} style={{ color: '#da1e28' }} />
              <p style={{ color: textPrimary, fontWeight: 600, fontSize: '1.125rem' }}>Analysis Failed</p>
              <p style={{ color: textSecondary, textAlign: 'center' }}>{aiError}</p>
              <Button kind="secondary" onClick={handleAIAnalysis}>
                Try Again
              </Button>
            </div>
          ) : aiAnalysis ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Confidence Indicator */}
              {aiAnalysis.confidence && (
                <div style={{ 
                  padding: '12px 16px',
                  backgroundColor: isDark ? '#262626' : '#f4f4f4',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Analytics size={20} style={{ color: '#0f62fe' }} />
                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>
                    Confidence: {Math.round(aiAnalysis.confidence * 100)}%
                  </span>
                  {aiAnalysis.cached && (
                    <Tag type="cool-gray" size="sm">Cached Response</Tag>
                  )}
                </div>
              )}

              {/* Intelligence contexts monitored via gateways */}
              {(aiAnalysis.contextsUsed?.length > 0 || aiAnalysis.devicesWithoutContext?.length > 0) && (
                <div>
                  <h4 style={{
                    color: textPrimary,
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <Information size={20} style={{ color: '#8a3ffc' }} />
                    Intelligence Context
                  </h4>
                  {aiAnalysis.contextsUsed?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                      {aiAnalysis.contextsUsed.map((ctx, index) => (
                        <div
                          key={`${ctx.gatewayName}-${ctx.deviceName}-${index}`}
                          style={{
                            padding: '10px 12px',
                            backgroundColor: isDark ? '#262626' : '#f4f4f4',
                            borderRadius: '6px',
                            borderLeft: `3px solid ${ctx.critical ? '#da1e28' : '#24a148'}`,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                            <span>{ctx.contextIcon}</span>
                            <strong style={{ color: textPrimary }}>{ctx.deviceName}</strong>
                            <Tag type="blue" size="sm">{ctx.gatewayName}</Tag>
                            <Tag type={ctx.critical ? 'red' : 'green'} size="sm">{ctx.contextLabel}</Tag>
                          </div>
                          <p style={{ color: textSecondary, fontSize: '0.8125rem', margin: 0 }}>
                            Expected: {ctx.tempRange}
                            {ctx.temperature != null ? ` · Reading: ${Number(ctx.temperature).toFixed(1)}°C` : ''}
                            {ctx.status ? ` · ${ctx.status}` : ''}
                          </p>
                          {ctx.customNotes && (
                            <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '6px 0 0' }}>{ctx.customNotes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {aiAnalysis.devicesWithoutContext?.length > 0 && (
                    <p style={{ color: textSecondary, fontSize: '0.8125rem', margin: 0 }}>
                      Connected sensors without context: {aiAnalysis.devicesWithoutContext.join(', ')}.
                      Assign context on the Intelligence page for compliance-focused gateway insights.
                    </p>
                  )}
                </div>
              )}

              {/* Main Analysis */}
              <div>
                <h4 style={{ 
                  color: textPrimary, 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Analytics size={20} style={{ color: '#0f62fe' }} />
                  Analysis Summary
                </h4>
                <p style={{ 
                  color: textSecondary, 
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {aiAnalysis.analysis}
                </p>
              </div>

              {/* Insights */}
              {aiAnalysis.insights && aiAnalysis.insights.length > 0 && (
                <div>
                  <h4 style={{ 
                    color: textPrimary, 
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <LightFilled size={20} style={{ color: '#f1c21b' }} />
                    Key Insights
                  </h4>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '0',
                    listStyle: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {aiAnalysis.insights.map((insight, index) => (
                      <li 
                        key={index}
                        style={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: isDark ? '#262626' : '#f4f4f4',
                          borderRadius: '4px'
                        }}
                      >
                        <LightFilled size={16} style={{ color: '#f1c21b', flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ color: textSecondary }}>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
                <div>
                  <h4 style={{ 
                    color: textPrimary, 
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Checkmark size={20} style={{ color: '#24a148' }} />
                    Recommendations
                  </h4>
                  <ul style={{ 
                    margin: 0, 
                    paddingLeft: '0',
                    listStyle: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    {aiAnalysis.recommendations.map((rec, index) => (
                      <li 
                        key={index}
                        style={{ 
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px',
                          padding: '8px 12px',
                          backgroundColor: isDark ? '#262626' : '#f4f4f4',
                          borderRadius: '4px'
                        }}
                      >
                        <Checkmark size={16} style={{ color: '#24a148', flexShrink: 0, marginTop: '2px' }} />
                        <span style={{ color: textSecondary }}>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </Modal>

      {/* Gateway Settings Modal */}
      <Modal
        open={settingsModalOpen}
        modalHeading={`Gateway Settings — ${
          selectedGatewayForSettings
            ? (getGatewayProductFamily(selectedGatewayForSettings) || selectedGatewayForSettings.gateway_product_code || selectedGatewayForSettings.name || 'Gateway')
            : 'Gateway'
        }`}
        primaryButtonText="Save Settings"
        secondaryButtonText="Cancel"
        onRequestClose={closeSettingsModal}
        onRequestSubmit={handleSaveSettings}
        primaryButtonDisabled={savingConfig || loadingConfig}
        size="lg"
      >
        {loadingConfig && !currentConfig ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px', gap: '16px' }}>
            <Loading description="Loading gateway configuration..." withOverlay={false} />
            <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
              Pulling live settings via MQTT getCfg (same as KGateway admin)...
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {selectedGatewayForSettings && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <GatewayTypeTags gateway={selectedGatewayForSettings} />
                  <p style={{ color: textSecondary, fontSize: '0.75rem', marginTop: '8px', marginBottom: 0 }}>
                    {configSource ? configSourceLabel(configSource) : 'Configuration source unknown'}
                    {loadingConfig ? ' · refreshing from gateway…' : ''}
                  </p>
                </div>
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={Renew}
                  disabled={loadingConfig || savingConfig}
                  onClick={() => loadGatewayConfigForModal(selectedGatewayForSettings, { forceRefresh: true })}
                >
                  Reload from gateway
                </Button>
              </div>
            )}

            {configError && (
              <InlineNotification
                kind="warning"
                title="Configuration notice"
                subtitle={configError}
                lowContrast
                hideCloseButton
              />
            )}

            {(() => {
              const caps = getGatewayAdminCapabilities(selectedGatewayForSettings);
              const parsed = parseGatewayConfigFields(currentConfig || {}, selectedGatewayForSettings);
              return (
                <>
                  {caps.isKGateway && (
                    <div>
                      <Dropdown
                        id="connectivity-type"
                        titleText="Connectivity Type"
                        label="Select connectivity type"
                        items={['MQTT', 'HTTP']}
                        selectedItem={connectivityType === 'http' ? 'HTTP' : 'MQTT'}
                        onChange={({ selectedItem }) => {
                          if (selectedItem) {
                            setConnectivityType(selectedItem.toLowerCase());
                          }
                        }}
                        disabled={savingConfig}
                      />
                      <p style={{ color: textSecondary, fontSize: '0.75rem', marginTop: '8px' }}>
                        {connectivityType === 'http'
                          ? 'Gateway will send data via HTTP POST requests'
                          : 'Gateway will send data via MQTT messages'}
                      </p>
                    </div>
                  )}

                  <div>
                    <NumberInput
                      id="upload-interval"
                      label={connectivityType === 'http' ? 'HTTP upload interval (seconds)' : 'Scan / upload interval (seconds)'}
                      value={uploadInterval}
                      min={10}
                      max={3600}
                      step={1}
                      onChange={(e, { value }) => {
                        const num = parseInt(value, 10) || 120;
                        setUploadInterval(Math.max(10, Math.min(3600, num)));
                      }}
                      disabled={savingConfig}
                    />
                    <p style={{ color: textSecondary, fontSize: '0.75rem', marginTop: '8px' }}>
                      Maps to <span className="mono">rptPeriod</span>
                      {caps.isEsp ? ' / ingest_interval_sec' : ''} on the gateway ({Math.round(uploadInterval / 60 * 10) / 10} min)
                    </p>
                  </div>

                  {(connectivityType === 'mqtt' || caps.isEsp) && (
                    <>
                      <NumberInput
                        id="filter-rssi"
                        label="RSSI filter (dBm)"
                        helperText="Ignore BLE advertisements weaker than this (KGateway filterrssi)"
                        value={filterRssi}
                        min={-120}
                        max={0}
                        step={1}
                        onChange={(e, { value }) => {
                          const num = parseInt(value, 10);
                          setFilterRssi(Number.isFinite(num) ? num : -100);
                        }}
                        disabled={savingConfig}
                      />

                      <TextInput
                        id="filter-mac"
                        labelText="MAC filter"
                        helperText="Regex or prefix allowlist (filtermac / mac_allowlist)"
                        placeholder="e.g. ^28:2C|^A0:B7"
                        value={filterMac}
                        onChange={(e) => setFilterMac(e.target.value)}
                        disabled={savingConfig}
                      />

                      <TextInput
                        id="filter-name"
                        labelText={caps.isKGateway ? 'BLE name filter (filterdata1)' : 'BLE name filter (filtername)'}
                        helperText="Regex pattern on advertised BLE name"
                        placeholder={caps.isKGateway ? 'e.g. ^KBPro|^KBeacon' : 'e.g. ^TH|^BL|^KBeacon'}
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        disabled={savingConfig}
                      />

                      {caps.isEsp && (
                        <Dropdown
                          id="filter-mode"
                          titleText="Filter mode"
                          label="Filter mode"
                          items={FILTER_MODE_ITEMS.map((item) => item.label)}
                          selectedItem={
                            FILTER_MODE_ITEMS.find((item) => item.id === filterMode)?.label
                            || FILTER_MODE_ITEMS[0].label
                          }
                          onChange={({ selectedItem }) => {
                            const match = FILTER_MODE_ITEMS.find((item) => item.label === selectedItem);
                            if (match) setFilterMode(match.id);
                          }}
                          disabled={savingConfig}
                        />
                      )}
                    </>
                  )}

                  {(currentConfig || parsed.firmwareVersion) && (
                    <div style={{
                      padding: '12px',
                      backgroundColor: isDark ? '#262626' : '#f4f4f4',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                    }}>
                      <p style={{ color: textSecondary, marginBottom: '8px', fontWeight: 600 }}>
                        Live configuration summary
                      </p>
                      <p style={{ color: textSecondary, margin: '0 0 4px' }}>
                        Type: {parsed.connectivityType === 'http' ? 'HTTP' : 'MQTT'} · Interval: {parsed.uploadInterval}s · RSSI: {parsed.filterRssi} dBm
                      </p>
                      {parsed.firmwareVersion && (
                        <p style={{ color: textSecondary, margin: '0 0 4px' }}>
                          Firmware: <span className="mono">{parsed.firmwareVersion}</span>
                        </p>
                      )}
                      {caps.isEsp && (
                        <p style={{ color: textSecondary, margin: 0 }}>
                          Filter mode: {parsed.filterMode}
                          {parsed.scanTimeSec != null ? ` · scan_time_sec: ${parsed.scanTimeSec}` : ''}
                        </p>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            {savingConfig && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: textSecondary }}>
                <Loading small withOverlay={false} />
                <span>Sending configuration to gateway via MQTT...</span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GatewayListPage;
